import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const CLOSE_ANIMATION_MS = 240;
const NUDGE_ANIMATION_MS = 360;
const ENTER_ANIMATION_MS = 260; // ≥ the 220ms zoom-in, with a small buffer

export type ModalSize = "sm" | "md" | "lg" | "xl";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  contentClassName?: string;
  'data-testid'?: string;
}

interface ModalHeaderProps {
  children: React.ReactNode;
  onClose?: () => void;
  showCloseButton?: boolean;
  className?: string;
  actions?: React.ReactNode;
  titleAction?: React.ReactNode;
}

interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

const ModalContext = React.createContext<{ onClose: () => void } | null>(null);

const Modal: React.FC<ModalProps> & {
  Header: React.FC<ModalHeaderProps>;
  Body: React.FC<ModalBodyProps>;
  Footer: React.FC<ModalFooterProps>;
} = ({
  isOpen,
  onClose,
  children,
  size = "md",
  closeOnOverlayClick = false,
  closeOnEscape = false,
  contentClassName = "",
  "data-testid": dataTestId,
}) => {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  // Becomes true once the entrance (zoom-in) animation has finished. After that
  // the content no longer carries the entrance animation, so playing/removing
  // the nudge shake can never re-trigger the zoom-in "re-pop".
  const [hasEntered, setHasEntered] = useState(false);
  // Brief shake played when the user tries to dismiss a modal that blocks
  // backdrop-click / ESC — signals the click registered without losing data.
  const [isNudging, setIsNudging] = useState(false);
  const nudgeTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerNudge = React.useCallback(() => {
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    setIsNudging(true);
    nudgeTimer.current = setTimeout(() => setIsNudging(false), NUDGE_ANIMATION_MS);
  }, []);

  useEffect(() => () => {
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      setHasEntered(false);
      // Drop the entrance animation once it has played, so a later nudge
      // (which removes its class) can't make the modal re-pop.
      const enterTimer = setTimeout(() => setHasEntered(true), ENTER_ANIMATION_MS);
      return () => clearTimeout(enterTimer);
    }
    if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, CLOSE_ANIMATION_MS);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (!shouldRender) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closeOnEscape) {
        onClose();
      } else if (e.key === "Escape" && !closeOnEscape) {
        e.preventDefault();
        e.stopPropagation();
        triggerNudge();
      }
    };
    document.addEventListener("keydown", handleEsc, true);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc, true);
      document.body.style.overflow = originalOverflow;
    };
  }, [shouldRender, onClose, closeOnEscape, triggerNudge]);

  if (!shouldRender) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    } else if (!closeOnOverlayClick && e.target === e.currentTarget) {
      e.stopPropagation();
      triggerNudge();
    }
  };

  return createPortal(
    <ModalContext.Provider value={{ onClose }}>
      <div
        className={`app-modal-overlay${isClosing ? " app-modal-overlay--closing" : ""}${closeOnOverlayClick ? " app-modal-overlay--dismissable" : ""}`}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        data-testid={dataTestId}
      >
        <div
          className={`app-modal-content app-modal-content--${size}${hasEntered ? " app-modal-content--entered" : ""}${isClosing ? " app-modal-content--closing" : ""}${isNudging ? " app-modal-content--nudge" : ""} ${contentClassName}`.trim()}
        >
          {children}
        </div>
      </div>
    </ModalContext.Provider>,
    document.body,
  );
};

const ModalHeader: React.FC<ModalHeaderProps> = ({
  children,
  onClose,
  showCloseButton = true,
  className = "",
  actions,
  titleAction,
}) => {
  const ctx = React.useContext(ModalContext);
  const handleClose = onClose ?? ctx?.onClose;

  return (
    <div className={`app-modal-header ${className}`.trim()}>
      <div className="app-modal-title-wrap">
        <h3 className="app-modal-title">{children}</h3>
        {titleAction && (
          <div className="app-modal-title-action">{titleAction}</div>
        )}
      </div>
      <div className="app-modal-header-right">
        {actions}
        {showCloseButton && handleClose && (
          <button
            type="button"
            data-testid="BTN_modal_header_close"
            onClick={handleClose}
            className="app-modal-close"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

const ModalBody: React.FC<ModalBodyProps> = ({ children, className = "" }) => (
  <div className={`app-modal-body ${className}`.trim()}>{children}</div>
);

const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  className = "",
}) => <div className={`app-modal-footer ${className}`.trim()}>{children}</div>;

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
